import { MenuItem } from '@szhsin/react-menu';
import { useCallback } from 'react';
import useCreateMarketTradeTape from '@paradigm/features/src/unified-rfqs/ui/hooks/useCreateMarketTradeTape';
import {
  UnifiedRfqTradeTapeProvider,
  useMaybeTradeTapeItem,
} from '@paradigm/features/src/unified-rfqs/contexts/unified-trade-tapes';
import { useBottomTabsMethods } from '@paradigm/features/src/unified-rfqs/ui/bottom-tabs';
import { useIsFeatureFlagEnabled } from '@paradigm/features/src/feature-flags/store';
import ContextMenuProvider, {
  ContextMenu as BaseContextMenu,
  useContextMenuActions,
  useContextMenuView,
} from '#/components/UnifiedRfqs/context-menu/context-menu';
import TradeDetailsModal from '#/components/UnifiedRfqs/trade-tape/TradeDetailsModal';
import TradeTapeContent from '#/components/UnifiedRfqs/trade-tape/TradeTapeContent';
import TapeSnapshotMirror from '#/components/UnifiedRfqs/trade-tape/TapeSnapshotMirror';
import { LocalTabFiltersProvider } from '#/components/UnifiedRfqs/UnifiedRfqs';
import { useBlockTerminalPendingStore } from '#/features/terminal/pending-message-store';
import { useTapeSnapshotStore } from '#/features/terminal/tape-snapshot-store';
import {
  buildTradeAnalysisContext,
  buildTradeAnalysisPrompt,
} from '#/features/terminal/build-trade-prompt';
import { useTerminalDrawerActions } from '#/features/terminal/drawer-context';
import {
  TrackingCtxProvider,
  useTrackAction,
  useTrackedCallback,
} from '@paradigm/features/src/analytics';
import CreateMarketMenuItem from '#/components/UnifiedRfqs/context-menu/CreateMarketMenuItem';

function ContextMenu() {
  const { openDetailsModal, createRfq: createRfqFetch } =
    useContextMenuActions();
  const { createMarket: createRfqTrade } = useCreateMarketTradeTape();
  const { detailsItemId } = useContextMenuView();
  const trade = useMaybeTradeTapeItem(detailsItemId ?? '');
  const trackAction = useTrackAction();
  const isBlockTerminalEnabled = useIsFeatureFlagEnabled(
    'feature_block_terminal',
  );
  const { setTab } = useBottomTabsMethods();
  const setPending = useBlockTerminalPendingStore((s) => s.setPending);
  const drawerActions = useTerminalDrawerActions();

  const createRfq = useCallback(
    (type: 'RFQ' | 'OB') => {
      trackAction('create market', { auctionType: type });

      if (trade?.rfqType === 'drfq') {
        createRfqTrade(type, trade);
        return;
      }

      createRfqFetch(type);
    },
    [createRfqFetch, createRfqTrade, trackAction, trade],
  );

  const handleViewDetails = useTrackedCallback(
    'view trade recap',
    openDetailsModal,
  );

  const handleAnalyzeInTerminal = useCallback(() => {
    if (trade == null) return;
    // Visible prompt in the bubble; full trade data + the last tape snapshot
    // (mirrored by TapeSnapshotMirror) ride as hidden context. Read the
    // snapshot imperatively at click time — no need to re-render the menu on
    // every tape tick.
    setPending(
      buildTradeAnalysisPrompt(trade),
      buildTradeAnalysisContext(
        trade,
        useTapeSnapshotStore.getState().snapshot,
      ),
    );
    setTab('block-terminal');
    // The terminal (and its agent connection) only mounts while the drawer
    // is open in side-panel mode, so open it — otherwise the pending
    // message sits undelivered until the user opens the panel themselves.
    // Harmless in bottom-tab mode, where the tab switch above is what
    // reveals the terminal.
    drawerActions.open();
  }, [trade, setPending, setTab, drawerActions]);

  const datadogPrefix = 'Trade Tape Table Context Menu';

  return (
    <BaseContextMenu>
      <CreateMarketMenuItem
        type="RFQ"
        datadogPrefix={datadogPrefix}
        onClick={() => createRfq('RFQ')}
      />
      <CreateMarketMenuItem
        type="OB"
        datadogPrefix={datadogPrefix}
        onClick={() => createRfq('OB')}
      />
      <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
      {isBlockTerminalEnabled && trade != null && (
        <MenuItem onClick={handleAnalyzeInTerminal}>
          Analyze in Terminal
        </MenuItem>
      )}
    </BaseContextMenu>
  );
}

export default function TradeTape() {
  return (
    <TrackingCtxProvider value="trade tape tab">
      <ContextMenuProvider>
        <LocalTabFiltersProvider storageKey="trade-tape-rfq-tab-filters">
          <UnifiedRfqTradeTapeProvider>
            <TapeSnapshotMirror />
            <TradeDetailsModal />
            <TradeTapeContent />
            <ContextMenu />
          </UnifiedRfqTradeTapeProvider>
        </LocalTabFiltersProvider>
      </ContextMenuProvider>
    </TrackingCtxProvider>
  );
}
